import React from "react";
import { Drawer } from "antd";
import { Modal } from "antd";
import { Collapse } from "antd";
import { Form, Button, Icon } from "antd";
import { Timeline, Alert } from "antd";
import formatDate from "date-fns/lightFormat";
import get from "lodash/get";

import LayoutDefault from "../../shared/LayoutDefault";

import ScenarioEditor from "../editor/ScenarioEditor";
import ScenarioTable from "./ScenarioTable";
import ScenarioViewer from "./ScenarioViewer";
import ScenarioForm from "./ScenarioForm";
import MediaSelector from "../media/MediaSelector";

const ScenarioView = ({
  data: { me: { user } = {}, scenarioSet, licenseSet, loading: dataLoading },
  match,

  loading,

  create = undefined,
  onCreateRequested = () => {},
  onCreateCancelled = () => {},
  onCreateSubmitted = async (values) => {},

  view = undefined,
  onViewRequested = (row) => {},
  onViewCancelled = () => {},

  viewSessionData = undefined,
  onViewSessionDataRequested = (row) => {},
  onViewSessionDataCancelled = () => {},

  edit = undefined,
  onEditRequested = (row) => {},
  onEditCancelled = () => {},
  onEditSubmitted = async (values) => {},

  editScenario = undefined,
  scenarioDefinitionUpdate = undefined,
  onScenarioEditorRequested = (row) => {},
  onScenarioEditorCancelled = () => {},
  onScenarioEditorSubmitted = async (definition) => {},

  onDeleteSubmitted = async (row) => {},

  mediaSelection,
  onMediaSelectionRequested = () => {},
  onMediaSelectionCancelled = () => {},
  onMediaSelectionSubmitted = () => {},
}) => (
  <LayoutDefault>
    {!dataLoading && (
      <React.Fragment>
        <Form.Item>
          <Button size="small" onClick={onCreateRequested} loading={loading}>
            <Icon type="plus" />
            Create a scenario
          </Button>
        </Form.Item>

        <ScenarioTable
          user={user}
          scenarioSet={scenarioSet}
          licenseSet={licenseSet}
          loading={loading}
          onShow={onViewRequested}
          onShowSessionData={onViewSessionDataRequested}
          onEdit={onEditRequested}
          onDelete={onDeleteSubmitted}
        />

        <Drawer
          visible={!!create}
          title="Create scenario"
          onClose={onCreateCancelled}
          width="70%"
        >
          {create && (
            <React.Fragment>
              <ScenarioForm
                user={user}
                row={create}
                rowUpdate={
                  scenarioDefinitionUpdate && {
                    definition: scenarioDefinitionUpdate,
                  }
                }
                licenseSet={licenseSet}
                loading={loading}
                onSubmit={onCreateSubmitted}
                onScenarioEditorRequested={onScenarioEditorRequested}
              />
              <Modal
                visible={!!editScenario}
                onCancel={onScenarioEditorCancelled}
                maskClosable={false}
                title={editScenario && `Edit scenario`}
                footer={null}
                centered={true}
                width={"90vw"}
              >
                {editScenario && (
                  <ScenarioEditor
                    definition={editScenario}
                    onSubmit={onScenarioEditorSubmitted}
                  />
                )}
              </Modal>
            </React.Fragment>
          )}
        </Drawer>

        <Drawer
          visible={!!view}
          title={view && `View ${view.name}`}
          onClose={onViewCancelled}
          width="70%"
        >
          {view && <ScenarioViewer row={view} />}
        </Drawer>

        <Drawer
          visible={!!edit}
          title={edit && `Edit ${edit.name}`}
          onClose={onEditCancelled}
          width="70%"
        >
          {edit && (
            <React.Fragment>
              <ScenarioForm
                user={user}
                row={edit}
                rowUpdate={
                  scenarioDefinitionUpdate && {
                    definition: scenarioDefinitionUpdate,
                  }
                }
                licenseSet={licenseSet}
                loading={loading}
                onSubmit={onEditSubmitted}
                onScenarioEditorRequested={onScenarioEditorRequested}
              />
              <Modal
                visible={!!editScenario}
                onCancel={onScenarioEditorCancelled}
                maskClosable={false}
                title={editScenario && `Edit scenario`}
                footer={null}
                centered={true}
                width={"90vw"}
              >
                {editScenario && (
                  <ScenarioEditor
                    onMediaSelectionRequested={onMediaSelectionRequested}
                    definition={scenarioDefinitionUpdate || editScenario}
                    onSubmit={onScenarioEditorSubmitted}
                  />
                )}
              </Modal>
            </React.Fragment>
          )}
        </Drawer>

        <Modal
          visible={!!mediaSelection}
          title="Select media"
          onCancel={onMediaSelectionCancelled}
          footer={null}
          width="70%"
        >
          <React.Fragment>
            <MediaSelector
              user={user}
              licenseSet={licenseSet}
              onCancel={onMediaSelectionCancelled}
              onSubmit={onMediaSelectionSubmitted}
            />
          </React.Fragment>
        </Modal>

        <Modal
          visible={!!viewSessionData}
          title={viewSessionData && `Sessions for ${viewSessionData.name}`}
          onCancel={onViewSessionDataCancelled}
          footer={<Button onClick={onViewSessionDataCancelled}>Return</Button>}
        >
          {viewSessionData && (
            <Collapse>
              {viewSessionData.sessionDataSet.map((sessionData, index) => {
                const date = new Date(sessionData.insertedAt);
                const data = JSON.parse(sessionData.data);

                const scenarioTitle = viewSessionData.name;
                const leaderboardStats = get(
                  data,
                  "game.statistics.leaderboard.players"
                );
                const leaderboard = Object.keys(leaderboardStats)
                  .map((name) => ({ name, score: leaderboardStats[name] }))
                  .sort((a, b) => b.score - a.score);
                const players = Object.keys(leaderboardStats);
                const availableMetrics = get(
                  data,
                  "game.statistics.available_metrics",
                  []
                ).reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {});
                const metrics = get(
                  data,
                  "game.statistics.leaderboard.metrics"
                );

                const questionStats = get(data, "game.statistics.questions");
                const choiceStatsByQuestion = questionStats.reduce(
                  (acc, { id, players, choices }) => ({
                    ...acc,
                    [id]: { players, choices },
                  }),
                  {}
                );
                const eventDefs = get(data, "scenario.definition.events");
                const questions = eventDefs.reduce((questions, event) => {
                  if (event.type !== "question") {
                    return questions;
                  }

                  return [
                    ...questions,
                    {
                      ...event,
                      choices: event.choices.map((choice, index) => ({
                        ...choice,
                        chosenBy: get(
                          choiceStatsByQuestion,
                          [event.id, "choices", index],
                          0
                        ),
                      })),
                    },
                  ];
                }, []);

                return (
                  <Collapse.Panel
                    header={`${formatDate(date, "yyyy-MM-dd HH:mm")} (${
                      players.length
                    })`}
                    key={index}
                  >
                    <p>
                      <strong>Scenario:</strong> {scenarioTitle}
                    </p>
                    <p>
                      <strong>Date:</strong> {formatDate(date, "yyyy-MM-dd")}
                    </p>
                    <p>
                      <strong>Time:</strong> {formatDate(date, "HH:mm")}
                    </p>
                    <p>
                      <strong>Players:</strong> {players.length}
                    </p>

                    <p>
                      <strong>Leaderboard:</strong>
                    </p>
                    <ol>
                      {leaderboard.map(({ name, score }, index) => (
                        <li key={name}>
                          <strong>{name}</strong>: {score}
                          <ul>
                            {Object.entries(get(metrics, name, {})).map(
                              ([metric, value]) => (
                                <li key={metric}>
                                  <strong>
                                    {get(availableMetrics, metric, metric)}
                                  </strong>
                                  : {value}
                                </li>
                              )
                            )}
                          </ul>
                        </li>
                      ))}
                    </ol>

                    <p>
                      <strong>Questions:</strong>
                    </p>
                    <Timeline>
                      {questions.map((question, index) => (
                        <Timeline.Item key={index}>
                          <strong>{question.text}</strong>
                          <br />
                          {question.choices.map(
                            ({ content, points, chosenBy }, index) => (
                              <Alert
                                key={index}
                                type={points ? "success" : "info"}
                                message={
                                  <span>
                                    {chosenBy}: {content}
                                  </span>
                                }
                                style={{ marginBottom: ".3rem" }}
                              />
                            )
                          )}
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </Collapse.Panel>
                );
              })}
            </Collapse>
          )}
        </Modal>
      </React.Fragment>
    )}
  </LayoutDefault>
);

export default ScenarioView;
